/**
 * Ecclesiastes 4:9–10 — Gather residual Prayers into a chosen host.
 * capacity = host.maxLevel − host.level; each donor I feeds +1 level.
 */
import type { LayerId } from "../board/types";
import { LAYER_ORDER } from "../board/types";
import type { Presence } from "../cards/types";

export type PrayerRef = {
  presence: Presence;
  layer: LayerId;
  slot: number;
};

/** How many residual keeps this host can still absorb. */
export function gatherCapacity(host: Presence): number {
  if (host.side !== "player" || host.level <= 0) return 0;
  return Math.max(0, host.maxLevel - host.level);
}

export function isGatherHostEligible(host: Presence): boolean {
  return gatherCapacity(host) > 0;
}

export function isGatherDonor(
  p: Presence,
  hostInstanceId: string,
): boolean {
  return (
    p.side === "player" &&
    p.level === 1 &&
    p.instanceId !== hostInstanceId
  );
}

/** Farther from Will first (Mind → Heart → Will), then older. */
export function sortGatherDonors(donors: PrayerRef[]): PrayerRef[] {
  return [...donors].sort((a, b) => {
    const ai = LAYER_ORDER.indexOf(a.layer);
    const bi = LAYER_ORDER.indexOf(b.layer);
    if (ai !== bi) return ai - bi; // outer first
    return b.presence.age - a.presence.age;
  });
}

export function planGather(
  host: PrayerRef,
  allPrayers: PrayerRef[],
): {
  ok: boolean;
  capacity: number;
  donors: PrayerRef[];
  toLevel: number;
  reason?: string;
} {
  const capacity = gatherCapacity(host.presence);
  if (capacity <= 0) {
    return {
      ok: false,
      capacity: 0,
      donors: [],
      toLevel: host.presence.level,
      reason: "host is already at full strength",
    };
  }

  const pool = sortGatherDonors(
    allPrayers.filter((x) =>
      isGatherDonor(x.presence, host.presence.instanceId),
    ),
  );
  if (!pool.length) {
    return {
      ok: false,
      capacity,
      donors: [],
      toLevel: host.presence.level,
      reason: "no other residual (I) Prayers to gather",
    };
  }

  const donors = pool.slice(0, capacity);
  const toLevel = Math.min(
    host.presence.maxLevel,
    host.presence.level + donors.length,
  );
  return { ok: true, capacity, donors, toLevel };
}
